import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BsPeople, BsBullseye, BsGraphUpArrow, BsClock, BsExclamationTriangle, BsWhatsapp, BsMeta, BsFileEarmarkText } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import { formatNumber, formatPercentage, truncate } from "../../../../utils/analyticsDashboardFormat";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  WidgetTable,
  TableScroll,
  SubTabsBar,
  SubTabButton,
  StatusPill,
  EmptyStateBox,
  InfoNote,
  MetricTooltip,
} from "./Widgets.styles";

const CampaignEffectivenessWidget = ({ query }) => {
  const [subTab, setSubTab] = useState("trends");

  const { data, isLoading, error } = useQuery(
    ["campaign-effectiveness", query.companyId, query.startDate, query.endDate, query.channelIds, query.granularity],
    () => analyticsDashboardApi.getCampaignEffectiveness(query),
    { enabled: !!query.companyId }
  );

  const { data: campaignsResponse } = useQuery(
    ["campaigns-names", query.companyId, query.startDate, query.endDate],
    () => analyticsDashboardApi.getCampaigns(query),
    { enabled: !!query.companyId }
  );

  const campaignNames = useMemo(() => {
    const mapping = {};
    if (campaignsResponse?.success && Array.isArray(campaignsResponse.data)) {
      campaignsResponse.data.forEach((c) => {
        if (c?.campaignId && c?.campaignName) mapping[c.campaignId] = c.campaignName;
      });
    }
    return mapping;
  }, [campaignsResponse]);

  const getCampaignDisplayName = (campaignId) => campaignNames[campaignId] || `Campaña ${campaignId}`;

  if (isLoading) {
    return (
      <MetricsGrid columns={4}>
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i}><SkeletonBlock style={{ height: 48 }} /></MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar datos de efectividad de campañas</div>
      </EmptyStateBox>
    );
  }

  if (!data?.data) {
    return <EmptyStateBox>No hay datos de efectividad de campañas disponibles para el período seleccionado.</EmptyStateBox>;
  }

  const { summary, timeSeries, topCampaigns } = data.data;
  const enrichedTopCampaigns = topCampaigns.map((c) => ({
    ...c,
    campaignDisplayName: getCampaignDisplayName(c.campaignId),
    campaignDisplayNameShort: truncate(getCampaignDisplayName(c.campaignId), 15),
  }));

  const tooltipStyle = {
    contentStyle: {
      background: "white",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
      fontFamily: "'Inter', sans-serif",
      fontSize: "0.82rem",
    },
    labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
    itemStyle: { color: "#4B5563" },
  };

  return (
    <WidgetSection>
      {/* Aclaración explícita de las plataformas incluidas en este consolidado */}
      <InfoNote>
        Las métricas de Efectividad de Campañas consolidan datos de tres plataformas:&nbsp;
        <strong><BsWhatsapp style={{ verticalAlign: "middle" }} /> WhatsApp</strong>,&nbsp;
        <strong><BsMeta style={{ verticalAlign: "middle" }} /> Facebook Ads</strong> y&nbsp;
        <strong><BsFileEarmarkText style={{ verticalAlign: "middle" }} /> Facebook Forms</strong>.
        Los valores muestran el Total Combinado de las tres fuentes.
      </InfoNote>

      <MetricsGrid columns={4}>
        <MetricCard accentColor="#3b82f6">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Alcanzadas
              <MetricTooltip text="Total Combinado (WhatsApp + Facebook Ads + Facebook Forms): conversaciones únicas que recibieron al menos un mensaje de campaña en el período." />
            </MetricLabel>
            <MetricIconWrap bg="#EFF6FF" color="#3b82f6"><BsPeople /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalClientsWithCampaigns)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Finalizadas
              <MetricTooltip text="Total Combinado (WhatsApp + Facebook Ads + Facebook Forms): conversaciones que, tras recibir una campaña, fueron cerradas exitosamente por un agente." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#10b981"><BsBullseye /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalFinishedClients)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#8b5cf6">
          <MetricCardHeader>
            <MetricLabel>
              Tasa de Conversión
              <MetricTooltip text="Total Combinado (WhatsApp + Facebook Ads + Facebook Forms): porcentaje de conversaciones finalizadas respecto a las alcanzadas: (Finalizadas / Alcanzadas) × 100." />
            </MetricLabel>
            <MetricIconWrap bg="#F5F3FF" color="#8b5cf6"><BsGraphUpArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(summary.overallConversionRate)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#f59e0b">
          <MetricCardHeader>
            <MetricLabel>
              Total de Campañas
              <MetricTooltip text="Número de campañas distintas activas durante el período seleccionado, sumando WhatsApp, Facebook Ads y Facebook Forms." />
            </MetricLabel>
            <MetricIconWrap bg="#FFFBEB" color="#f59e0b"><BsClock /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalCampaigns)}</MetricValue>
        </MetricCard>
      </MetricsGrid>

      <SubTabsBar>
        <SubTabButton active={subTab === "trends"} onClick={() => setSubTab("trends")}>Tendencias</SubTabButton>
        <SubTabButton active={subTab === "campaigns"} onClick={() => setSubTab("campaigns")}>Mejores Campañas</SubTabButton>
        <SubTabButton active={subTab === "data"} onClick={() => setSubTab("data")}>Tabla de Datos</SubTabButton>
      </SubTabsBar>

      {subTab === "trends" && (
        <ChartCard>
          <ChartCardTitle>
            Tendencias de Efectividad de Campañas
            <MetricTooltip text="Evolución diaria de conversaciones alcanzadas y finalizadas por campañas, con la tasa de conversión superpuesta en el eje derecho." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={timeSeries} margin={{ top: 8, right: 24, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
              <Bar yAxisId="left" dataKey="totalClientsWithCampaigns" fill="#3b82f6" name="Conv. Alcanzadas" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="totalFinishedClients" fill="#10b981" name="Conv. Finalizadas" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke="#8b5cf6" strokeWidth={2.5} name="Tasa de Conversión %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {subTab === "campaigns" && (
        <ChartCard>
          <ChartCardTitle>
            Mejores Campañas
            <MetricTooltip text="Las campañas con mayor número de conversaciones alcanzadas. Cada barra muestra alcanzadas vs finalizadas; la línea muestra la tasa de conversión." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={480}>
            <BarChart data={enrichedTopCampaigns} margin={{ top: 8, right: 24, left: 0, bottom: 120 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="campaignDisplayNameShort" angle={-45} textAnchor="end" height={120} interval={0} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip
                labelFormatter={(label) => {
                  const item = enrichedTopCampaigns.find((c) => c.campaignDisplayNameShort === label);
                  return item ? `${item.campaignDisplayName} (ID: ${item.campaignId})` : label;
                }}
                {...tooltipStyle}
              />
              <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
              <Bar yAxisId="left" dataKey="clientsReached" fill="#3b82f6" name="Conv. Alcanzadas" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="clientsFinished" fill="#10b981" name="Conv. Finalizadas" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke="#8b5cf6" strokeWidth={2.5} name="Tasa de Conversión %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {subTab === "data" && (
        <ChartCard>
          <ChartCardTitle>
            Datos de Rendimiento de Campañas
            <MetricTooltip text="Total Combinado de todas las plataformas: WhatsApp, Facebook Ads y Facebook Forms. La columna 'Plataforma' muestra la fuente inferida a partir del identificador de la campaña." />
          </ChartCardTitle>
          <TableScroll>
            <WidgetTable>
              <thead>
                <tr>
                  <th>Nombre de Campaña</th>
                  <th>Plataforma</th>
                  <th className="align-right">Conv. Alcanzadas</th>
                  <th className="align-right">Conv. Finalizadas</th>
                  <th className="align-right">Tasa de Conversión</th>
                </tr>
              </thead>
              <tbody>
                {topCampaigns.map((c) => {
                  const displayName = getCampaignDisplayName(c.campaignId);
                  // Inferir plataforma por prefijo de campaignId o nombre resuelto
                  const platform = (() => {
                    const id = (c.campaignId || "").toLowerCase();
                    const name = displayName.toLowerCase();
                    if (id.startsWith("wa_") || name.includes("whatsapp")) return { label: "WhatsApp", icon: <BsWhatsapp />, color: "#25D366" };
                    if (id.startsWith("fb_form") || id.startsWith("form_") || name.includes("form")) return { label: "Facebook Forms", icon: <BsFileEarmarkText />, color: "#1877F2" };
                    if (id.startsWith("fb_") || id.startsWith("act_") || name.includes("facebook") || name.includes("ads")) return { label: "Facebook Ads", icon: <BsMeta />, color: "#1877F2" };
                    return { label: "Combinado", icon: null, color: "#6B7280" };
                  })();
                  return (
                    <tr key={c.campaignId}>
                      <td>
                        <div>{displayName}</div>
                        <div style={{ fontSize: "0.7rem", color: "#9CA3AF" }}>{c.campaignId}</div>
                      </td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: platform.color, fontWeight: 600 }}>
                          {platform.icon} {platform.label}
                        </span>
                      </td>
                      <td className="align-right">{formatNumber(c.clientsReached)}</td>
                      <td className="align-right">{formatNumber(c.clientsFinished)}</td>
                      <td className="align-right">
                        <StatusPill variant={c.conversionRate >= 50 ? "good" : c.conversionRate >= 25 ? "warn" : "bad"}>
                          {formatPercentage(c.conversionRate)}
                        </StatusPill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </WidgetTable>
          </TableScroll>
        </ChartCard>
      )}
    </WidgetSection>
  );
};

export default CampaignEffectivenessWidget;
